import { useCallback, useMemo, useState } from 'react';

import { groupApi } from '@/api/services/groupApi';
import { viewApi } from '@/api/services/viewApi';
import type { ChinaAppSysGroup, ChinaAppSysGroupView } from '@/types/entities';

import { useAdminChanges } from '../adminChanges';
import { PickerDialog, type PickerOption } from '../PickerDialog';
import { useAdminList } from './adminList';
import { AdminSubList } from './AdminSubList';

/**
 * Gruppen, die eine View sehen (REQ-29 §2.2). Im Altclient war die Zuordnung nur von der
 * Gruppe aus pflegbar (`IwmAdminListGroupViews.razor`); die Endpunkte der Gegenrichtung
 * gab es in der API schon.
 */
export interface AdminViewGroupsListProps {
  parentId: number;
}

/** Spaltenüberschriften wie in der Gruppenliste eines Benutzers. */
const COLUMNS = ['Role', 'Administrative'] as const;

/**
 * Die Rechte, mit denen eine neue Zuordnung entsteht — `AddGroupToView` übernimmt den
 * Rumpf unverändert und belegt nichts vor (`ViewsController.cs`). Die Werte sind deshalb
 * dieselben, die `IwmAdminListGroupViews.razor` von der Gruppe aus setzt: sehen und
 * bearbeiten, nicht anlegen, nicht löschen, kein Audit. Die einzelnen Rechte stehen
 * danach unter „Permissions" bei der Gruppe.
 */
function createGroupView(viewId: number, groupId: number): ChinaAppSysGroupView {
  return {
    Id: 0,
    // `default(DateTime)` so, wie System.Text.Json den Wert serialisiert; eine leere
    // Zeichenkette lehnt die API als `DateTime` ab.
    Created: '0001-01-01T00:00:00',
    CreatedByUserId: 0,
    Modified: '0001-01-01T00:00:00',
    ModifiedByUserId: 0,
    Timestamp: null,
    AllowAudit: false,
    AllowCreate: false,
    AllowDelete: false,
    AllowEdit: true,
    AllowRegiezentrum: true,
    GroupId: groupId,
    GroupBezeichnung: null,
    Group: null,
    // `AddGroupToView` antwortet mit 400, wenn `ViewId` nicht zur Route passt.
    ViewId: viewId,
    ViewBezeichnung: null,
    View: null,
  };
}

const RIGHTS_HINT =
  'Die einzelnen Rechte (Add, Update, Delete, Visible in Menu, Audit) stehen unter „Permissions" bei der Gruppe. Eine neue Zuordnung erlaubt zunächst Update und Visible in Menu.';

export function AdminViewGroupsList(props: AdminViewGroupsListProps) {
  const { parentId } = props;
  // Nicht destrukturieren: `AdminChanges` deklariert die Meldung als Methode, ein
  // herausgelöster Verweis stößt @typescript-eslint/unbound-method an.
  const adminChanges = useAdminChanges();
  const load = useCallback(() => viewApi.getGroups(parentId), [parentId]);
  const { rows, loading, error, reload } = useAdminList(load);
  const [options, setOptions] = useState<PickerOption[] | null>(null);
  const [busy, setBusy] = useState(false);

  const taken = useMemo(() => rows.map((group) => group.Id), [rows]);

  // Angeboten werden nur Gruppen ohne Zugang. Geladen wird erst beim Öffnen — und vor dem
  // Öffnen, damit der Dialog nicht kurz „nichts zur Auswahl" zeigt.
  const openPicker = async () => {
    setBusy(true);
    const all = await groupApi.get();
    setOptions(
      all
        .filter((group) => !taken.includes(group.Id))
        .map((group) => ({
          id: group.Id,
          label: group.Bezeichnung ?? '',
          description: group.IstAdministrativ ? 'administrativ' : undefined,
        })),
    );
    setBusy(false);
  };

  const change = async (run: () => Promise<unknown>) => {
    setBusy(true);
    await run();
    await reload();
    setBusy(false);
    // Die Gruppen einer View entscheiden, wer sie im Menü sieht.
    adminChanges.notifyNavigationChanged();
  };

  return (
    <>
      <AdminSubList<ChinaAppSysGroup>
        title="Gruppen"
        columns={COLUMNS}
        rows={rows}
        rowKey={(group) => group.Id}
        renderCells={(group) => (
          <>
            <td>{group.Bezeichnung}</td>
            <td>{group.IstAdministrativ ? 'ja' : 'nein'}</td>
          </>
        )}
        loading={loading}
        error={error}
        onRetry={() => {
          void reload();
        }}
        busy={busy}
        onAdd={() => {
          void openPicker();
        }}
        onRemove={(group) => {
          void change(() => viewApi.removeGroup(parentId, group.Id));
        }}
        emptyText="Keine Gruppen zugeordnet"
        errorMessage="Die Gruppen konnten nicht geladen werden."
        hint={RIGHTS_HINT}
      />
      <PickerDialog
        open={options !== null}
        title="Gruppen auswählen"
        options={options ?? []}
        emptyText="Alle Gruppen haben bereits Zugang."
        onClose={() => {
          setOptions(null);
        }}
        onSubmit={async (ids) => {
          await change(async () => {
            for (const id of ids) await viewApi.addGroup(parentId, createGroupView(parentId, id));
          });
        }}
      />
    </>
  );
}

export default AdminViewGroupsList;
